import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl, formatDate, formatIDR, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Plane, MapPin, ShoppingCart, ArrowLeft, Plus, Check, MessageSquarePlus, Lock, ChevronLeft, ChevronRight, Settings, X, Package, Users, ClipboardList, Download, FileText, FileSpreadsheet, Share2, Printer } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import { exportShoppingPDF, exportShoppingExcel, exportShoppingWhatsApp, exportShoppingPrint } from "@/lib/exportShopping";

export default function TripDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [buyerFeePct, setBuyerFeePct] = useState(null);
  const [jastiperFeePct, setJastiperFeePct] = useState(null);
  // Owner-only: catalog management modal
  const [manageOpen, setManageOpen] = useState(false);
  const [allCatalog, setAllCatalog] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [savingCatalog, setSavingCatalog] = useState(false);
  // Owner-only: incoming orders summary
  const [ordersSummary, setOrdersSummary] = useState(null);
  const [summaryTab, setSummaryTab] = useState("aggregated"); // "aggregated" | "by_buyer"

  useEffect(() => {
    api.get(`/trips/${id}`).then((r) => setTrip(r.data)).finally(() => setLoading(false));
    api.get("/fee-config").then((r) => {
      setBuyerFeePct(Number(r.data?.buyer_platform_fee_percent ?? 0));
      setJastiperFeePct(Number(r.data?.jastiper_platform_fee_percent ?? 0));
    }).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!user || user.role !== "buyer" || !trip) return;
    api.get(`/cart/${id}`).then((r) => {
      const count = (r.data?.items || []).reduce((acc, it) => acc + (it.qty || 1), 0);
      setCartCount(count);
    }).catch(() => {});
  }, [user, trip, id]);

  // Owner-only: cancel trip modal
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Owner: load incoming orders summary
  useEffect(() => {
    if (!user || !trip || user.id !== trip.jastiper_id) return;
    api.get(`/trips/${id}/orders-summary`)
      .then((r) => setOrdersSummary(r.data))
      .catch(() => {});
  }, [user, trip, id]);

  const submitCancelTrip = async () => {
    const reason = cancelReason.trim();
    if (reason.length < 5) {
      toast.error("Alasan minimal 5 karakter");
      return;
    }
    setCancelling(true);
    try {
      const r = await api.post(`/trips/${id}/cancel`, { reason });
      toast.success(`Trip dibatalkan. ${r.data.affected_orders} order kena dampak, ${r.data.refunds_pending} pending refund.`);
      setCancelOpen(false);
      // refresh trip
      const t2 = await api.get(`/trips/${id}`);
      setTrip(t2.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal membatalkan trip");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>Memuat...</div>;
  if (!trip) return <div className="text-center py-20" style={{color: "var(--app-muted)"}}>{t("trips.not_found")}</div>;

  const isOwner = user?.id === trip.jastiper_id;
  const canBuy = user && user.role === "buyer" && !isOwner;

  const addToCart = async (item) => {
    setBusy(true);
    try {
      const r = await api.post("/cart/add", { trip_id: id, catalog_item_id: item.id, qty: 1 });
      setCartCount(r.data?.count ?? cartCount + 1);
      toast.success(`${item.name} ditambahkan ke keranjang`);
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menambahkan");
    } finally {
      setBusy(false);
    }
  };

  const openManageCatalog = async () => {
    setManageOpen(true);
    try {
      const r = await api.get("/catalog");
      setAllCatalog(r.data || []);
      const current = new Set((trip.catalog_items || []).map((i) => i.id));
      setSelectedIds(current);
    } catch {
      toast.error("Gagal memuat katalog");
    }
  };

  const toggleSelect = (itemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      return next;
    });
  };

  const saveCatalog = async () => {
    setSavingCatalog(true);
    try {
      const r = await api.put(`/trips/${id}/catalog`, { catalog_item_ids: Array.from(selectedIds) });
      setTrip(r.data);
      setManageOpen(false);
      toast.success(`Katalog trip diperbarui (${selectedIds.size} item)`);
    } catch (e) {
      toast.error(formatApiError(e) || "Gagal menyimpan katalog");
    } finally {
      setSavingCatalog(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="trip-detail-page">
      <div className="flex justify-between items-center">
        <Link to="/trips" className="text-sm flex items-center gap-2" style={{color: "var(--app-muted)"}} data-testid="back-link">
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("trips.active_trips")}
        </Link>
        {canBuy && (
          <Link
            to={`/cart/${id}`}
            className="relative inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md"
            style={{borderColor: "var(--app-border)"}}
            data-testid="trip-go-to-cart-btn"
          >
            <ShoppingCart size={14} strokeWidth={1.5} />
            <span className="hidden sm:inline">{t("cart.title") || "Keranjang"}</span>
            {cartCount > 0 && (
              <span
                className="ml-1 px-1.5 py-0.5 text-[10px] font-mono rounded-full text-white"
                style={{backgroundColor: "var(--app-brand)"}}
                data-testid="trip-cart-count-badge"
              >
                {cartCount}
              </span>
            )}
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="label-caps mb-2 flex items-center gap-2">
              <MapPin size={12} strokeWidth={1.5} /> {trip.destination_country}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">
              {trip.destination_city ? `${trip.destination_city}, ` : ""}{trip.destination_country}
            </h1>
            <div className="font-mono text-sm mt-3" style={{color: "var(--app-muted)"}}>
              {formatDate(trip.departure_date)} → {formatDate(trip.return_date)}
            </div>
            {trip.origin_location && (
              <div className="text-sm flex items-center gap-1.5 mt-2" style={{color: "var(--app-muted)"}}>
                <Plane size={12} strokeWidth={1.5} /> {t("trips.departs_from") || "berangkat dari"} {trip.origin_location}
              </div>
            )}
          </div>

          {trip.notes && (
            <div className="surface p-5">
              <div className="label-caps mb-2">{t("trips.notes_label")}</div>
              <p className="text-sm leading-relaxed">{trip.notes}</p>
            </div>
          )}

          {isOwner && ordersSummary && (
            <OwnerOrdersSummary
              trip={trip}
              summary={ordersSummary}
              tab={summaryTab}
              setTab={setSummaryTab}
            />
          )}

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl tracking-tight">Katalog</h2>
              <span className="label-caps">{trip.catalog_items?.length || 0} item</span>
            </div>
            {(!trip.catalog_items || trip.catalog_items.length === 0) ? (
              <div className="surface p-12 text-center text-sm" style={{color: "var(--app-muted)"}}>
                Belum ada katalog. Anda bisa kirim permintaan custom.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 stagger-fade">
                {trip.catalog_items.map((it) => {
                  const fv = parseFloat(it.fee_value || 0);
                  const fee = fv > 0 ? (it.fee_type === "percent" ? parseFloat(it.price) * fv / 100 : fv) : 0;
                  const finalPrice = parseFloat(it.price || 0) + fee;
                  const isHidden = it.hide_price || it.price_hidden;
                  const photos = (Array.isArray(it.photos) && it.photos.length) ? it.photos : (it.photo_path ? [it.photo_path] : []);
                  return (
                    <div key={it.id} className="grid-card flex flex-col" data-testid={`catalog-item-${it.id}`}>
                      <CatalogPhotoCarousel photos={photos} alt={it.name} itemId={it.id} isHidden={isHidden} />
                      <div className="p-3 flex-1 flex flex-col">
                        <div className="text-sm font-medium truncate">{it.name}</div>
                        {isHidden ? (
                          <div className="text-sm font-mono mt-1" style={{color: "var(--app-muted)"}} data-testid={`hidden-price-display-${it.id}`}>
                            Harga: ••••••
                          </div>
                        ) : (
                          <>
                            <div className="text-sm font-mono mt-1" style={{color: "var(--app-brand)"}}>{formatIDR(finalPrice)}</div>
                            {fee > 0 && <div className="text-[10px] font-mono" style={{color: "var(--app-muted)"}}>incl. jastiper fee</div>}
                          </>
                        )}
                        {canBuy && (
                          isHidden ? (
                            <Link
                              to={`/requests/new?jastiper_id=${trip.jastiper_id}&trip_id=${trip.id}`}
                              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-white rounded-md"
                              style={{backgroundColor: "var(--app-brand)"}}
                              data-testid={`request-price-${it.id}`}
                            >
                              <MessageSquarePlus size={12} strokeWidth={2} /> Minta Penawaran
                            </Link>
                          ) : (
                            <button
                              onClick={() => addToCart(it)}
                              disabled={busy}
                              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium border hover:bg-[var(--app-surface-alt)] rounded-md"
                              style={{borderColor: "var(--app-border)"}}
                              data-testid={`add-to-cart-${it.id}`}
                            >
                              <Plus size={12} strokeWidth={2} /> Tambah ke Keranjang
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="surface p-5">
            <div className="label-caps mb-3">Jastiper</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center font-display font-medium text-white" style={{backgroundColor: "var(--app-brand)"}}>
                {trip.jastiper_name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-medium inline-flex items-center gap-1">
                  {trip.jastiper_name}
                  {(trip.jastiper_verified_badge || trip.jastiper?.verified_badge) && (
                    <VerifiedBadge size={14} testid={`trip-detail-jastiper-verified-${trip.id}`} />
                  )}
                </div>
                <div className="text-xs font-mono" style={{color: "var(--app-muted)"}}>★ {trip.jastiper?.rating || 5.0}</div>
              </div>
            </div>
            <div className="border-t mt-4 pt-4 text-sm space-y-1.5" style={{borderColor: "var(--app-border)"}}>
              <div className="label-caps mb-1" style={{color: "var(--app-muted)"}}>{t("trips.platform_fee")}</div>
              <div className="flex justify-between" data-testid="platform-fee-buyer">
                <span style={{color: "var(--app-muted)"}}>{t("trips.fee_for_buyer")}</span>
                <span className="font-mono">{buyerFeePct != null ? `${buyerFeePct}%` : "—"}</span>
              </div>
              <div className="flex justify-between" data-testid="platform-fee-jastiper">
                <span style={{color: "var(--app-muted)"}}>{t("trips.fee_for_jastiper")}</span>
                <span className="font-mono">{jastiperFeePct != null ? `${jastiperFeePct}%` : "—"}</span>
              </div>
              <p className="text-[10px] pt-1" style={{color: "var(--app-muted)"}}>
                {t("trips.fee_split_note")}
              </p>
            </div>
          </div>

          {canBuy && (
            <>
              <Link
                to={`/cart/${id}`}
                className="btn-primary-flat w-full"
                data-testid="goto-cart-btn"
              >
                <ShoppingCart size={16} strokeWidth={1.5} /> Lihat Keranjang ({cartCount})
              </Link>
              <Link
                to={`/requests/new?jastiper_id=${trip.jastiper_id}&trip_id=${trip.id}`}
                className="btn-secondary-flat w-full text-sm"
                data-testid="request-custom-btn"
              >
                <MessageSquarePlus size={14} strokeWidth={1.5} /> Pesan Item Custom
              </Link>
            </>
          )}

          {!user && (
            <Link to="/auth" className="btn-primary-flat w-full" data-testid="signin-to-order">{t("trips.signin_to_order")}</Link>
          )}

          {isOwner && (
            <div className="surface p-4 space-y-3" data-testid="owner-trip-actions">
              {trip.status === "cancelled" ? (
                <div className="text-center py-2">
                  <div className="label-caps text-[10px] px-2 py-1 inline-block" style={{backgroundColor: "#FEE2E2", color: "#991B1B"}}>
                    TRIP DIBATALKAN
                  </div>
                  {trip.cancellation_reason && (
                    <p className="text-xs mt-2 italic" style={{color: "var(--app-muted)"}}>"{trip.cancellation_reason}"</p>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-center text-sm" style={{color: "var(--app-muted)"}}>
                    <Check size={20} strokeWidth={1.5} className="mx-auto mb-2" style={{color: "var(--app-success)"}} />
                    {t("trips.your_trip")}
                  </div>
                  <button
                    onClick={openManageCatalog}
                    className="btn-primary-flat w-full text-sm"
                    data-testid="manage-catalog-btn"
                  >
                    <Settings size={14} strokeWidth={1.5} /> {t("trips.manage_catalog_items", { count: trip.catalog_items?.length || 0 })}
                  </button>
                  <Link
                    to="/catalog"
                    className="block text-center text-xs hover:underline"
                    style={{color: "var(--app-brand)"}}
                    data-testid="goto-master-catalog-link"
                  >
                    {t("trips.add_new_to_master")}
                  </Link>
                  <button
                    onClick={() => { setCancelReason(""); setCancelOpen(true); }}
                    className="w-full text-xs py-2 px-3 border transition-colors hover:bg-[#FEE2E2]"
                    style={{borderColor: "#FCA5A5", color: "#B91C1C"}}
                    data-testid="cancel-trip-btn"
                  >
                    {t("trips.cancel_trip")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Catalog Management Modal — owner only */}
      {manageOpen && isOwner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
          style={{backgroundColor: "rgba(0,0,0,0.55)"}}
          data-testid="manage-catalog-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setManageOpen(false); }}
        >
          <div className="w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col" style={{backgroundColor: "var(--app-surface)", border: "1px solid var(--app-border)"}}>
            <div className="flex items-center justify-between p-3 sm:p-4 border-b" style={{borderColor: "var(--app-border)"}}>
              <div className="min-w-0 flex-1">
                <div className="label-caps" style={{color: "var(--app-muted)"}}>{t("trips.manage_catalog")}</div>
                <h3 className="font-display text-base sm:text-xl tracking-tight">{t("trips.pick_items_for_trip")}</h3>
              </div>
              <button
                onClick={() => setManageOpen(false)}
                className="p-2 hover:opacity-70 flex-shrink-0"
                data-testid="close-manage-modal"
              >
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>

            <div className="overflow-y-auto p-3 sm:p-4 flex-1">
              {allCatalog.length === 0 ? (
                <div className="text-center py-8" style={{color: "var(--app-muted)"}}>
                  <p className="text-sm mb-3">{t("trips.no_master_items")}</p>
                  <Link to="/catalog" className="btn-primary-flat text-sm inline-flex" data-testid="empty-modal-goto-catalog">
                    <Plus size={14} strokeWidth={1.5} /> {t("trips.open_my_catalog")}
                  </Link>
                </div>
              ) : (() => {
                // Iter 24 — Group by country, with trip's destination country pinned on top.
                const tripCountry = (trip.destination_country || "").trim();
                const groups = {};
                allCatalog.forEach((it) => {
                  const key = (it.country || "").trim() || t("trips.no_country");
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(it);
                });
                const otherCountries = Object.keys(groups).filter((c) => c !== tripCountry).sort();
                const orderedCountries = [
                  ...(groups[tripCountry] ? [tripCountry] : []),
                  ...otherCountries,
                ];
                return (
                  <div className="space-y-3" data-testid="manage-catalog-grouped">
                    {orderedCountries.map((country) => {
                      const items = groups[country];
                      const selectedInGroup = items.filter((i) => selectedIds.has(i.id)).length;
                      const isTripCountry = country === tripCountry;
                      return (
                        <details key={country} open={isTripCountry} className="border" style={{borderColor: isTripCountry ? "var(--app-brand)" : "var(--app-border)"}} data-testid={`country-group-${country}`}>
                          <summary
                            className="flex items-center justify-between gap-2 p-3 cursor-pointer hover:bg-[var(--app-surface-alt)] select-none"
                            style={{backgroundColor: isTripCountry ? "var(--app-brand-soft)" : "transparent"}}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <ChevronRight size={14} strokeWidth={1.5} className="group-open:rotate-90 transition-transform flex-shrink-0" />
                              <span className="font-medium truncate">{country}</span>
                              {isTripCountry && (
                                <span className="label-caps px-1.5 py-0.5 text-[9px] text-white" style={{backgroundColor: "var(--app-brand)"}}>
                                  {t("trips.trip_destination_country_badge")}
                                </span>
                              )}
                            </div>
                            <span className="text-xs font-mono flex-shrink-0" style={{color: "var(--app-muted)"}}>
                              {t("trips.items_selected_of", { sel: selectedInGroup, total: items.length })}
                            </span>
                          </summary>
                          <div className="divide-y" style={{borderColor: "var(--app-border)"}}>
                            {items.map((it) => {
                              const checked = selectedIds.has(it.id);
                              return (
                                <label
                                  key={it.id}
                                  className="flex items-center gap-3 p-3 cursor-pointer transition-colors hover:bg-[var(--app-surface-alt)]"
                                  style={{backgroundColor: checked ? "var(--app-brand-soft)" : "transparent"}}
                                  data-testid={`catalog-checkbox-row-${it.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSelect(it.id)}
                                    className="w-4 h-4 flex-shrink-0"
                                    data-testid={`catalog-checkbox-${it.id}`}
                                  />
                                  <div className="w-12 h-12 flex-shrink-0" style={{backgroundColor: "var(--app-surface-alt)"}}>
                                    {it.photo_path && <img src={fileUrl(it.photo_path)} alt={it.name} className="w-full h-full object-cover" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium truncate">{it.name}</div>
                                    <div className="text-xs font-mono" style={{color: "var(--app-muted)"}}>
                                      {formatIDR(it.price)}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </details>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="border-t p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3" style={{borderColor: "var(--app-border)"}}>
              <div className="text-xs sm:text-sm font-mono" style={{color: "var(--app-muted)"}} data-testid="manage-catalog-count">
                {t("trips.items_count_selected", { count: selectedIds.size })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setManageOpen(false)}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm border"
                  style={{borderColor: "var(--app-border)"}}
                  data-testid="cancel-manage-btn"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={saveCatalog}
                  disabled={savingCatalog}
                  className="btn-primary-flat text-sm flex-1 sm:flex-none justify-center"
                  data-testid="save-manage-catalog-btn"
                >
                  <Check size={14} strokeWidth={1.5} /> {savingCatalog ? t("common.saving") || "..." : t("common.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iter 25 — Cancel Trip Modal */}
      {cancelOpen && isOwner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          style={{backgroundColor: "rgba(0,0,0,0.55)"}}
          data-testid="cancel-trip-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setCancelOpen(false); }}
        >
          <div className="w-full max-w-md" style={{backgroundColor: "var(--app-surface)", border: "1px solid var(--app-border)"}}>
            <div className="flex items-center justify-between p-3 sm:p-4 border-b" style={{borderColor: "var(--app-border)"}}>
              <div>
                <div className="label-caps" style={{color: "#B91C1C"}}>{t("trips.cancel_trip")}</div>
                <h3 className="font-display text-base sm:text-xl tracking-tight">{t("trips.cancel_trip_title")}</h3>
              </div>
              <button onClick={() => setCancelOpen(false)} className="p-2 hover:opacity-70" data-testid="close-cancel-modal">
                <X size={16} strokeWidth={1.5} />
              </button>
            </div>
            <div className="p-3 sm:p-4 space-y-3">
              <p className="text-sm" style={{color: "var(--app-muted)"}}>
                {t("trips.cancel_trip_warning")}
              </p>
              <label className="block">
                <div className="label-caps mb-1.5" style={{color: "var(--app-muted)"}}>
                  {t("trips.cancel_reason_label")} <span style={{color: "#B91C1C"}}>*</span>
                </div>
                <textarea
                  className="input-flat min-h-[100px] resize-y"
                  placeholder={t("trips.cancel_reason_placeholder")}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength={500}
                  data-testid="cancel-reason-input"
                />
                <div className="text-[10px] text-right mt-1" style={{color: "var(--app-muted)"}}>{cancelReason.length}/500</div>
              </label>
              <p className="text-xs" style={{color: "var(--app-muted)"}}>
                {t("trips.cancel_trip_refund_note")}
              </p>
            </div>
            <div className="border-t p-3 sm:p-4 flex flex-col sm:flex-row gap-2" style={{borderColor: "var(--app-border)"}}>
              <button
                onClick={() => setCancelOpen(false)}
                className="px-4 py-2 text-sm border flex-1 sm:flex-none"
                style={{borderColor: "var(--app-border)"}}
                data-testid="dismiss-cancel-modal"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={submitCancelTrip}
                disabled={cancelling || cancelReason.trim().length < 5}
                className="px-4 py-2 text-sm text-white flex-1 sm:flex-none disabled:opacity-50"
                style={{backgroundColor: "#B91C1C"}}
                data-testid="confirm-cancel-trip-btn"
              >
                {cancelling ? "..." : t("trips.confirm_cancel_trip")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Iter 20 — Photo carousel for items with multiple variant photos (e.g. different colours).
 * Renders the main photo + small left/right chevrons + dot indicator + click-thumbnails-below
 * row when there are >1 photos. Falls back to plain image for single-photo items.
 */
function CatalogPhotoCarousel({ photos, alt, itemId, isHidden }) {
  const [idx, setIdx] = useState(0);
  if (!photos || photos.length === 0) {
    return (
      <div className="aspect-square overflow-hidden relative flex items-center justify-center" style={{backgroundColor: "var(--app-surface-alt)", color: "var(--app-muted)"}}>
        <ShoppingCart size={32} strokeWidth={1} />
        {isHidden && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-white" style={{backgroundColor: "var(--app-brand)"}} data-testid={`item-hidden-badge-${itemId}`}>
            <Lock size={10} strokeWidth={2} /> Harga Private
          </div>
        )}
      </div>
    );
  }
  const safeIdx = ((idx % photos.length) + photos.length) % photos.length;
  const go = (delta) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => i + delta);
  };
  return (
    <div data-testid={`item-photo-carousel-${itemId}`}>
      <div className="aspect-square overflow-hidden relative group" style={{backgroundColor: "var(--app-surface-alt)"}}>
        <img src={fileUrl(photos[safeIdx])} alt={alt} className="w-full h-full object-cover" />
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={go(-1)}
              className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 dark:bg-black/60 border flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{borderColor: "var(--app-border)"}}
              aria-label="Previous photo"
              data-testid={`carousel-prev-${itemId}`}
            >
              <ChevronLeft size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={go(1)}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 bg-white/90 dark:bg-black/60 border flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{borderColor: "var(--app-border)"}}
              aria-label="Next photo"
              data-testid={`carousel-next-${itemId}`}
            >
              <ChevronRight size={14} strokeWidth={2} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 px-2 py-0.5 rounded-full" style={{backgroundColor: "rgba(0,0,0,0.55)"}}>
              {photos.map((_, i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full"
                  style={{backgroundColor: i === safeIdx ? "white" : "rgba(255,255,255,0.45)"}}
                />
              ))}
            </div>
          </>
        )}
        {isHidden && (
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide text-white" style={{backgroundColor: "var(--app-brand)"}} data-testid={`item-hidden-badge-${itemId}`}>
            <Lock size={10} strokeWidth={2} /> Harga Private
          </div>
        )}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-1 px-2 pt-2 overflow-x-auto" data-testid={`carousel-thumbs-${itemId}`}>
          {photos.map((p, i) => (
            <button
              key={`${p}-${i}`}
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
              className="flex-shrink-0 w-10 h-10 border overflow-hidden"
              style={{
                borderColor: i === safeIdx ? "var(--app-brand)" : "var(--app-border)",
                boxShadow: i === safeIdx ? "0 0 0 1px var(--app-brand)" : "none",
              }}
              aria-label={`Photo ${i + 1}`}
              data-testid={`carousel-thumb-${itemId}-${i}`}
            >
              <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// Iter 23 — Owner-only orders panel: aggregated shopping list + per-buyer view
const STATUS_LABEL = {
  pending_payment: { text: "Menunggu Bayar", bg: "#FEF3C7", color: "#92400E" },
  paid: { text: "Sudah Dibayar", bg: "#DBEAFE", color: "#1E40AF" },
  purchased: { text: "Sudah Dibeli", bg: "#E0E7FF", color: "#3730A3" },
  shipping: { text: "Dikirim", bg: "#FCE7F3", color: "#9D174D" },
  arrived: { text: "Tiba", bg: "#DCFCE7", color: "#166534" },
  released: { text: "Selesai", bg: "#D1FAE5", color: "#065F46" },
  cancelled: { text: "Dibatalkan", bg: "#FEE2E2", color: "#991B1B" },
  disputed: { text: "Bermasalah", bg: "#FEE2E2", color: "#991B1B" },
};

function OwnerOrdersSummary({ trip, summary, tab, setTab }) {
  const { t } = useI18n();
  const { orders = [], aggregated_items: agg = [], active_orders = 0, total_orders = 0 } = summary;
  const [exportOpen, setExportOpen] = useState(false);

  const tripMeta = {
    destination_country: trip?.destination_country,
    destination_city: trip?.destination_city,
    departure_date: trip?.departure_date,
    return_date: trip?.return_date,
    jastiper_name: trip?.jastiper_name,
  };

  const doExport = async (fn) => {
    setExportOpen(false);
    try {
      await fn({ trip: tripMeta, items: agg });
    } catch (e) {
      toast.error(`${t("export.failed")}: ` + (e?.message || "unknown"));
    }
  };

  return (
    <div className="surface p-4 sm:p-5 space-y-4" data-testid="owner-orders-summary">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-caps" style={{color: "var(--app-muted)"}}>{t("trip.orders.title")}</div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">
            {t("trip.orders.active_count", { active: active_orders })}
            <span className="text-sm font-mono font-normal ml-2" style={{color: "var(--app-muted)"}}>
              {t("trip.orders.total_count", { total: total_orders })}
            </span>
          </h2>
        </div>

        {agg.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border hover:bg-[var(--app-surface-alt)] transition-colors w-full sm:w-auto justify-center sm:justify-start"
              style={{borderColor: "var(--app-border)"}}
              data-testid="export-shopping-btn"
            >
              <Download size={13} strokeWidth={1.5} /> {t("export.shopping_list")}
              <ChevronRight size={12} strokeWidth={1.5} className={`transition-transform ${exportOpen ? "rotate-90" : ""}`} />
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setExportOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-1 w-56 border shadow-lg z-50"
                  style={{backgroundColor: "var(--app-surface)", borderColor: "var(--app-border)"}}
                  data-testid="export-menu"
                >
                  <ExportMenuItem icon={FileText} label={t("export.pdf")} onClick={() => doExport(exportShoppingPDF)} testid="export-pdf" />
                  <ExportMenuItem icon={FileSpreadsheet} label={t("export.excel")} onClick={() => doExport(exportShoppingExcel)} testid="export-excel" />
                  <ExportMenuItem icon={Share2} label={t("export.whatsapp")} onClick={() => doExport(exportShoppingWhatsApp)} testid="export-whatsapp" />
                  <ExportMenuItem icon={Printer} label={t("export.print")} onClick={() => doExport(exportShoppingPrint)} testid="export-print" />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="py-8 text-center text-sm" style={{color: "var(--app-muted)"}} data-testid="no-incoming-orders">
          <Package size={28} strokeWidth={1.5} className="mx-auto mb-2 opacity-50" />
          {t("trip.orders.none")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-0 border" style={{borderColor: "var(--app-border)"}} data-testid="summary-tabs">
            <button
              type="button"
              onClick={() => setTab("aggregated")}
              className="p-3 text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 transition-colors"
              style={{
                borderRight: "1px solid var(--app-border)",
                backgroundColor: tab === "aggregated" ? "var(--app-brand)" : "transparent",
                color: tab === "aggregated" ? "#fff" : "var(--app-fg)",
              }}
              data-testid="tab-aggregated"
            >
              <ClipboardList size={14} strokeWidth={1.5} /> <span className="truncate">{t("trip.orders.tab_aggregated")}</span>
            </button>
            <button
              type="button"
              onClick={() => setTab("by_buyer")}
              className="p-3 text-xs sm:text-sm flex items-center justify-center gap-1.5 sm:gap-2 transition-colors"
              style={{
                backgroundColor: tab === "by_buyer" ? "var(--app-brand)" : "transparent",
                color: tab === "by_buyer" ? "#fff" : "var(--app-fg)",
              }}
              data-testid="tab-by-buyer"
            >
              <Users size={14} strokeWidth={1.5} /> <span className="truncate">{t("trip.orders.tab_by_buyer", { count: orders.length })}</span>
            </button>
          </div>

          {tab === "aggregated" ? (
            <div data-testid="aggregated-view" className="space-y-2">
              <p className="text-xs" style={{color: "var(--app-muted)"}}>
                {t("trip.orders.aggregated_desc")}
              </p>
              {agg.length === 0 ? (
                <div className="py-6 text-center text-xs" style={{color: "var(--app-muted)"}}>
                  {t("trip.orders.all_cancelled")}
                </div>
              ) : (
                <div className="divide-y border" style={{borderColor: "var(--app-border)"}}>
                  {agg.map((it, i) => (
                    <details key={i} className="group" data-testid={`agg-item-${i}`}>
                      <summary className="flex items-center gap-2 sm:gap-3 p-3 cursor-pointer hover:bg-[var(--app-surface-alt)]">
                        <div className="w-12 h-12 flex-shrink-0" style={{backgroundColor: "var(--app-surface-alt)"}}>
                          {it.photo_path && <img src={fileUrl(it.photo_path)} alt={it.name} className="w-full h-full object-cover" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm sm:text-base">{it.name}</div>
                          <div className="text-[10px] sm:text-xs font-mono" style={{color: "var(--app-muted)"}}>
                            {formatIDR(it.unit_price)} / {t("trip.orders.per_unit")} · {t("trip.orders.from_buyers", { count: it.buyers.length })}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-display text-xl sm:text-2xl tracking-tight" style={{color: "var(--app-brand)"}} data-testid={`agg-qty-${i}`}>
                            ×{it.total_qty}
                          </div>
                          <div className="text-[9px] sm:text-[10px] uppercase tracking-wider" style={{color: "var(--app-muted)"}}>{t("trip.orders.qty_total")}</div>
                        </div>
                        <ChevronRight size={14} strokeWidth={1.5} className="group-open:rotate-90 transition-transform flex-shrink-0 hidden sm:block" style={{color: "var(--app-muted)"}} />
                      </summary>
                      <div className="pl-12 sm:pl-16 pr-3 pb-3 space-y-1" style={{backgroundColor: "var(--app-surface-alt)"}}>
                        {it.buyers.map((b, bi) => {
                          const s = STATUS_LABEL[b.escrow_status] || { text: b.escrow_status, bg: "#F3F4F6", color: "#374151" };
                          return (
                            <Link
                              key={bi}
                              to={`/orders/${b.order_id}`}
                              className="flex items-center justify-between text-xs py-1.5 hover:opacity-80 gap-2"
                              data-testid={`agg-buyer-${i}-${bi}`}
                            >
                              <span className="truncate" style={{color: "var(--app-muted)"}}>{b.buyer_name}</span>
                              <span className="flex items-center gap-2 flex-shrink-0">
                                <span className="font-mono" style={{color: "var(--app-fg)"}}>×{b.qty}</span>
                                <span className="label-caps px-1.5 py-0.5 text-[9px]" style={{backgroundColor: s.bg, color: s.color}}>{t(`order.status.${b.escrow_status}`) !== `order.status.${b.escrow_status}` ? t(`order.status.${b.escrow_status}`) : s.text}</span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div data-testid="by-buyer-view" className="space-y-2">
              {orders.map((o) => {
                const s = STATUS_LABEL[o.escrow_status] || { text: o.escrow_status, bg: "#F3F4F6", color: "#374151" };
                return (
                  <Link
                    key={o.id}
                    to={`/orders/${o.id}`}
                    className="block border p-3 hover:bg-[var(--app-surface-alt)] transition-colors"
                    style={{borderColor: "var(--app-border)"}}
                    data-testid={`buyer-order-${o.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 sm:gap-3 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-display flex-shrink-0" style={{backgroundColor: "var(--app-brand)"}}>
                          {o.buyer_name?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate text-sm">{o.buyer_name || "—"}</div>
                          <div className="text-[10px] font-mono" style={{color: "var(--app-muted)"}}>{formatDate(o.created_at)}</div>
                        </div>
                      </div>
                      <span className="label-caps px-2 py-1 text-[9px] sm:text-[10px] flex-shrink-0" style={{backgroundColor: s.bg, color: s.color}}>{t(`order.status.${o.escrow_status}`) !== `order.status.${o.escrow_status}` ? t(`order.status.${o.escrow_status}`) : s.text}</span>
                    </div>
                    <div className="space-y-1 text-sm pl-0 sm:pl-10">
                      {(o.items || []).map((it, idx) => (
                        <div key={idx} className="flex items-center justify-between font-mono text-xs gap-2">
                          <span className="truncate" style={{color: "var(--app-muted)"}}>{it.name}</span>
                          <span className="flex-shrink-0 whitespace-nowrap">×{it.qty} <span style={{color: "var(--app-muted)"}}>· {formatIDR((it.price || 0) * (it.qty || 1))}</span></span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between text-xs font-mono" style={{borderColor: "var(--app-border)"}}>
                      <span style={{color: "var(--app-muted)"}}>{t("trip.orders.total_buyer_paid")}</span>
                      <span className="font-semibold">{formatIDR(o.gross_amount || 0)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function ExportMenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-[var(--app-surface-alt)] transition-colors"
      data-testid={testid}
    >
      <Icon size={14} strokeWidth={1.5} style={{color: "var(--app-brand)"}} />
      <span>{label}</span>
    </button>
  );
}
